/**
 * Spec in, frames out.
 *
 * Deterministic by construction: no clock, no randomness, no iteration over an
 * unordered map. The same spec produces the same bytes today and in two years,
 * which is what makes a regenerated asset folder something git can tell you
 * the truth about.
 */

import { encodePng } from "./png.js";
import { Canvas, shifted } from "./raster.js";
import { SHAPE_FIELDS, SHAPES } from "./shapes.js";
import { framesOf, resolveShift, validateSpec, wrapOf, type ShiftRef, type SpriteSpec } from "./spec.js";

export interface RenderedFrame {
	index: number;
	canvas: Canvas;
}

/**
 * `limit` renders only the first few frames.
 *
 * For anything that keeps the sprite, leave it off: a caller that wants the
 * animation wants all of it. It is for showing a still of something animated,
 * where rendering the other seven frames to throw them away is most of the
 * work. The welcome page assembles a sprite one part at a time and re-renders
 * on every part, so this is the difference between eight renders per step and
 * one.
 */
export function renderFrames(spec: SpriteSpec, limit?: number): RenderedFrame[] {
	const { kit, parts } = validateSpec(spec, SHAPE_FIELDS);
	const [width, height] = spec.size;
	const frames = framesOf(spec);

	const wrap = wrapOf(spec);

	return (limit === undefined ? frames : frames.slice(0, limit)).map((frame) => {
		const canvas = new Canvas(width, height, wrap);
		for (const part of parts) {
			if (part.on && !(part.on as number[]).includes(frame.index)) continue;
			// A shifted part draws through a view of the same canvas that is the
			// same size and reports the same edges, and only moves the pixels on
			// their way down. That is what lets a shift work on every shape
			// without any of them knowing about it.
			const shift = part.shift as [ShiftRef, ShiftRef] | undefined;
			const surface = shift
				? shifted(canvas, resolveShift(shift[0], frame), resolveShift(shift[1], frame))
				: canvas;

			SHAPES[part.shape].draw({ canvas: surface, kit, spec, frame }, part);
		}

		if (spec.outline !== false) {
			const outlineRole = typeof spec.outline === "string" ? spec.outline : "outline";
			canvas.outline(kit.roles[outlineRole]);
		}
		return { index: frame.index, canvas };
	});
}

export interface RenderedFile {
	filename: string;
	bytes: Uint8Array;
}

/**
 * Filenames follow the convention the game already loads: a single frame is
 * `name.png`, an animation is `name_1.png` upward. Changing that would mean
 * touching GDScript for no gain.
 */
export function renderFiles(spec: SpriteSpec): RenderedFile[] {
	const frames = renderFrames(spec);
	const files = frames.map(({ index, canvas }) => ({
		filename: frames.length === 1 ? `${spec.name}.png` : `${spec.name}_${index + 1}.png`,
		bytes: encodePng(canvas.toBitmap()),
	}));
	const still = spec.frames?.still;
	if (still !== undefined && frames.length > 1) {
		files.push({ filename: `${spec.name}.png`, bytes: encodePng(frames[still].canvas.toBitmap()) });
	}
	return files;
}

/**
 * The first frame of a sprite, as pixels.
 *
 * Six places wanted exactly this and each spelled it out, three of them
 * rendering every frame of an animation and then throwing all but one away.
 * Asking for one frame is both what they meant and cheaper.
 */
export const bitmapOf = (spec: SpriteSpec) => renderFrames(spec, 1)[0].canvas.toBitmap();
